import type { FC, ReactNode } from 'react'
import type { AppointmentStatus } from '@/types/clinic'

import { useEffect, useMemo, useState } from 'react'
import { useRouter } from 'next/router'
import {
    CalendarDays,
    CalendarPlus,
    CalendarRange,
    ChevronLeft,
    ChevronRight,
    LayoutGrid,
    List
} from 'lucide-react'
import { Seo } from '@/components/generals/seo'
import { useClinic } from '@/lib/clinic/store'
import { byAppointmentTime, isOpenAppointment } from '@/lib/clinic/selectors'
import { useLookups } from '@/hooks/use-clinic-helpers'
import { useAppDialogs } from '@/components/dialogs/app-dialogs'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import PageHeader from '@/components/app/page-header'
import PageLoading from '@/components/app/page-loading'
import SearchInput from '@/components/app/search-input'
import SimpleSelect from '@/components/app/simple-select'
import { StatusBadge, TONE_CLASSES } from '@/components/app/status-badge'
import AppointmentList from '@/components/appointments/appointment-list'
import CalendarTimeGrid from '@/components/appointments/calendar-time-grid'
import CalendarMonth from '@/components/appointments/calendar-month'

import {
    addDays,
    addMonths,
    formatDate,
    formatLongDate,
    formatMonthYear,
    isISODate,
    startOfWeek
} from '@/lib/dates'

import { APPOINTMENT_STATUSES, APPOINTMENT_STATUS_META } from '@/constants/clinic'

type View = 'list' | 'day' | 'week' | 'month'
type Range = 'upcoming' | 'today' | 'week' | 'past' | 'all' | 'custom'

const VIEWS: Array<{ value: View, label: string, icon: typeof List }> = [
    { value: 'list', label: 'List', icon: List },
    { value: 'day', label: 'Day', icon: CalendarDays },
    { value: 'week', label: 'Week', icon: CalendarRange },
    { value: 'month', label: 'Month', icon: LayoutGrid }
]

const RANGE_OPTIONS: Array<{ value: Range, label: string }> = [
    { value: 'upcoming', label: 'Today and upcoming' },
    { value: 'today', label: 'Today' },
    { value: 'week', label: 'This week' },
    { value: 'past', label: 'Past appointments' },
    { value: 'all', label: 'All dates' },
    { value: 'custom', label: 'Custom dates' }
]

const AppointmentsPage: FC = (): ReactNode => {
    const router = useRouter()
    const { ready, data, today } = useClinic()
    const { patientsById } = useLookups()
    const { openAppointment } = useAppDialogs()

    const [view, setView] = useState<View>('day')
    const [cursor, setCursor] = useState('')
    const [query, setQuery] = useState('')
    const [status, setStatus] = useState('all')
    const [range, setRange] = useState<Range>('upcoming')
    const [from, setFrom] = useState('')
    const [to, setTo] = useState('')

    // Allow links such as /appointments?view=week&date=2026-11-02
    useEffect(() => {
        if (!router.isReady) return
        const requestedView = router.query.view
        const requestedDate = router.query.date
        if (typeof requestedView === 'string' && VIEWS.some(item => item.value === requestedView)) setView(requestedView as View)
        if (typeof requestedDate === 'string' && isISODate(requestedDate)) setCursor(requestedDate)
    }, [router.isReady, router.query.view, router.query.date])

    const anchor = cursor || today

    const filtered = useMemo(() => {
        const value = query.trim().toLowerCase()
        const digits = value.replace(/\D/g, '')
        return data.appointments.filter(item => {
            if (status === 'open' && !isOpenAppointment(item)) return false
            if (status !== 'all' && status !== 'open' && item.status !== status) return false
            if (!value) return true
            const patient = patientsById.get(item.patientId)
            return Boolean(patient?.name.toLowerCase().includes(value)
                || patient?.code.toLowerCase().includes(value)
                || item.treatmentName.toLowerCase().includes(value)
                || (digits.length >= 3 && item.phone.includes(digits)))
        })
    }, [data.appointments, query, status, patientsById])

    const listItems = useMemo(() => {
        const weekStart = startOfWeek(today)
        const weekEnd = addDays(weekStart, 6)
        const inRange = filtered.filter(item => {
            switch (range) {
                case 'upcoming': return item.date >= today
                case 'today': return item.date === today
                case 'week': return item.date >= weekStart && item.date <= weekEnd
                case 'past': return item.date < today
                case 'custom': return item.date >= (from || '0000-01-01') && item.date <= (to || '9999-12-31')
                default: return true
            }
        })
        const sorted = [...inRange].sort(byAppointmentTime)
        return range === 'past' ? sorted.reverse() : sorted
    }, [filtered, range, from, to, today])

    if (!ready) {
        return (
            <>
                <Seo title='Appointments' />
                <PageLoading />
            </>
        )
    }

    const weekStart = startOfWeek(anchor)
    const weekDays = Array.from({ length: 7 }, (_, index) => addDays(weekStart, index))

    const move = (direction: -1 | 1) => {
        if (view === 'day') setCursor(addDays(anchor, direction))
        if (view === 'week') setCursor(addDays(anchor, direction * 7))
        if (view === 'month') setCursor(addMonths(anchor, direction))
    }

    const rangeLabel = view === 'day'
        ? formatLongDate(anchor)
        : view === 'week'
            ? `${formatDate(weekDays[0])} to ${formatDate(weekDays[6])}`
            : formatMonthYear(anchor)

    const dayItems = filtered.filter(item => item.date === anchor)
    const todayCount = data.appointments.filter(item => item.date === today && item.status !== 'cancelled').length
    const upcomingCount = data.appointments.filter(item => item.date > today && isOpenAppointment(item)).length

    return (
        <>
            <Seo title='Appointments' description='Day, week and month calendar and list of clinic appointments.' />

            <PageHeader
                title='Appointments'
                description={`${todayCount} today and ${upcomingCount} upcoming. Click an empty slot in the calendar to book it.`}
                actions={(
                    <Button onClick={() => openAppointment({ date: view === 'list' ? today : anchor })}>
                        <CalendarPlus aria-hidden='true' />
                        Book appointment
                    </Button>
                )}
            />

            <Card className='gap-0 py-0'>
                <div className='flex flex-col gap-3 border-b p-4 xl:flex-row xl:items-center xl:justify-between'>
                    <Tabs value={view} onValueChange={value => setView(value as View)}>
                        <TabsList>
                            {VIEWS.map(item => {
                                const Icon = item.icon
                                return (
                                    <TabsTrigger key={item.value} value={item.value} className='px-3'>
                                        <Icon aria-hidden='true' />
                                        {item.label}
                                    </TabsTrigger>
                                )
                            })}
                        </TabsList>
                    </Tabs>

                    {view === 'list'
                        ? (
                            <div className='flex flex-wrap items-center gap-2'>
                                <SimpleSelect value={range} onValueChange={value => setRange(value as Range)} options={RANGE_OPTIONS} ariaLabel='Date range' className='w-56' />
                                {range === 'custom' && (
                                    <>
                                        <Input type='date' value={from} onChange={event => setFrom(event.target.value)} aria-label='From date' className='w-auto' />
                                        <Input type='date' value={to} min={from || undefined} onChange={event => setTo(event.target.value)} aria-label='To date' className='w-auto' />
                                    </>
                                )}
                            </div>
                        )
                        : (
                            <div className='flex flex-wrap items-center gap-2'>
                                <div className='flex items-center gap-1'>
                                    <Button variant='outline' size='icon' onClick={() => move(-1)} aria-label={`Previous ${view}`}>
                                        <ChevronLeft aria-hidden='true' />
                                    </Button>
                                    <Button variant='outline' onClick={() => setCursor(today)}>Today</Button>
                                    <Button variant='outline' size='icon' onClick={() => move(1)} aria-label={`Next ${view}`}>
                                        <ChevronRight aria-hidden='true' />
                                    </Button>
                                </div>
                                <p className='min-w-0 text-sm font-semibold text-foreground' aria-live='polite'>{rangeLabel}</p>
                                <Input
                                    type='date'
                                    value={anchor}
                                    onChange={event => event.target.value && setCursor(event.target.value)}
                                    aria-label='Jump to date'
                                    className='w-auto'
                                />
                            </div>
                        )}
                </div>

                <div className='grid gap-3 border-b p-4 sm:grid-cols-[minmax(0,1fr)_14rem]'>
                    <SearchInput value={query} onChange={setQuery} placeholder='Search by patient, phone, ID or treatment' />
                    <SimpleSelect
                        value={status}
                        onValueChange={setStatus}
                        options={[
                            { value: 'all', label: 'All statuses' },
                            { value: 'open', label: 'Open bookings only' },
                            ...APPOINTMENT_STATUSES.map(item => ({ value: item, label: APPOINTMENT_STATUS_META[item].label }))
                        ]}
                        ariaLabel='Filter by status'
                    />
                </div>

                {view === 'list' && (
                    <AppointmentList
                        appointments={listItems}
                        emptyAction={<Button onClick={() => openAppointment()}>Book appointment</Button>}
                    />
                )}

                {view === 'day' && (
                    <>
                        <div className='flex flex-wrap items-center gap-2 border-b px-4 py-3'>
                            {dayItems.length === 0
                                ? <p className='text-sm text-muted-foreground'>No appointments on this day. Click a time slot to book one.</p>
                                : APPOINTMENT_STATUSES.map(item => {
                                    const count = dayItems.filter(entry => entry.status === item).length
                                    if (!count) return null
                                    return <StatusBadge key={item} tone={APPOINTMENT_STATUS_META[item].tone} label={`${count} ${APPOINTMENT_STATUS_META[item].label}`} />
                                })}
                        </div>
                        <CalendarTimeGrid days={[anchor]} appointments={filtered} onSlotClick={(date, time) => openAppointment({ date, time })} />
                    </>
                )}

                {view === 'week' && (
                    <CalendarTimeGrid days={weekDays} appointments={filtered} onSlotClick={(date, time) => openAppointment({ date, time })} />
                )}

                {view === 'month' && (
                    <CalendarMonth
                        anchor={anchor}
                        appointments={filtered}
                        onSelectDay={date => {
                            setCursor(date)
                            setView('day')
                        }}
                        onBook={date => openAppointment({ date })}
                    />
                )}

                {view !== 'list' && (
                    <ul className='flex flex-wrap items-center gap-x-4 gap-y-2 border-t px-4 py-3 text-xs text-muted-foreground' aria-label='Status colours'>
                        {APPOINTMENT_STATUSES.map(item => (
                            <li key={item} className='flex items-center gap-1.5'>
                                <span className={`size-2.5 rounded-sm ${TONE_CLASSES[APPOINTMENT_STATUS_META[item].tone].dot}`} aria-hidden='true' />
                                {APPOINTMENT_STATUS_META[item].label}
                            </li>
                        ))}
                    </ul>
                )}
            </Card>
        </>
    )
}

export default AppointmentsPage
